import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Clock, Phone } from "lucide-react";
import { CLINIC, HERO_IMAGE } from "../data/clinic";
import { scrollToId } from "../lib/scroll";

const LINES = ["Compassionate.", "Trusted.", "Always open."];

const lineContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.35 } },
};
const lineVariant = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1.05, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section
      ref={ref}
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen pt-28 pb-16 sm:pt-32 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-8 items-center">
        {/* Left: kinetic type */}
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center gap-3 mb-7"
          >
            <div className="flex items-center gap-1.5 rounded-full bg-sa-beige px-4 py-2 border border-sa-espresso/5">
              <Clock className="w-4 h-4 text-sa-bronze" />
              <span className="font-body text-sm font-semibold text-sa-espresso">
                Open 24 Hours
              </span>
              <span className="font-body text-xs text-sa-mocha">
                · Every day, including emergencies
              </span>
            </div>
          </motion.div>

          <motion.h1
            variants={lineContainer}
            initial="hidden"
            animate="show"
            className="font-display font-light text-sa-espresso text-6xl sm:text-7xl lg:text-[6.2rem] leading-[0.98] tracking-tight"
          >
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden py-1">
                <motion.span
                  variants={lineVariant}
                  className={`block ${i === 2 ? "italic text-sa-bronze" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1 }}
            className="font-body text-base sm:text-lg text-sa-mocha max-w-md mt-8 leading-relaxed"
          >
            {CLINIC.tagline} We care for cats, dogs and birds — 24 hours a day,
            every day, with a team that treats your pet like family.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="flex flex-wrap items-center gap-4 mt-10"
          >
            <button
              onClick={() => scrollToId("#booking")}
              data-testid="hero-book-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-sa-bronze pl-7 pr-5 py-4 font-body text-sm font-semibold text-sa-cream hover:bg-sa-bronzedark hover:scale-[1.03] active:scale-95 transition-[transform,background-color] duration-300"
            >
              Book Appointment
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <a
              href={`tel:${CLINIC.phoneTel}`}
              data-testid="hero-call-btn"
              className="inline-flex items-center gap-2 rounded-full border border-sa-espresso/30 px-6 py-4 font-body text-sm font-semibold text-sa-espresso hover:bg-sa-espresso hover:text-sa-cream transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              {CLINIC.phoneDisplay}
            </a>
          </motion.div>
        </div>

        {/* Right: parallax clipped hero image with spotlight */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <div className="relative rounded-[36px] overflow-hidden aspect-[4/5] shadow-[0_30px_80px_-20px_rgba(59,42,32,0.35)]">
            <motion.img
              src={HERO_IMAGE}
              alt="A veterinarian caring for a cat at MS Pet Hospital"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-[122%] object-cover"
            />
            {/* espresso spotlight vignette */}
            <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_70%_20%,transparent_30%,rgba(59,42,32,0.45)_100%)]" />
          </div>

          {/* floating caption chip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.3 }}
            className="absolute -bottom-6 -left-4 sm:left-auto sm:-right-6 bg-sa-cream rounded-2xl px-5 py-4 shadow-[0_12px_40px_rgba(59,42,32,0.15)] border border-sa-espresso/5 max-w-[210px]"
          >
            <p className="font-display text-lg text-sa-espresso leading-snug">
              24/7
            </p>
            <p className="font-body text-sm text-sa-mocha">Emergency care, any hour</p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
